import React, { useState, useEffect } from "react";
import {
  FaFileUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTrash,
  FaFilePdf,
  FaTimes,
} from "react-icons/fa";
import {
  getComplianceData,
  updateComplianceData,
  uploadPoliceCertificate,
  deletePoliceCertificate,
  uploadCertificateOfCurrency,
  deleteCertificateOfCurrency,
  getDocumentSignedUrl,
} from "../services/complianceService";

const LCHS_FACT_SHEET_TEXT = `
Aged Care Provider Training Fact Sheet

This fact sheet outlines the key requirements and responsibilities for aged care service providers.

Key Requirements:
1. All franchisees must maintain current police check certificates
2. Annual influenza vaccination is strongly recommended for all staff working with elderly clients
3. Understanding and compliance with aged care quality standards
4. Adherence to infection control protocols
5. Respect for client dignity, privacy, and independence
6. Proper documentation and reporting procedures

By acknowledging this fact sheet, you confirm that you have read and understood these requirements and will comply with all aged care service standards.
`;

const ComplianceTab = ({ userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showFactSheet, setShowFactSheet] = useState(false);

  // Compliance state - current values
  const [complianceData, setComplianceData] = useState({
    policeCertificate: null,
    policeCertificateExpiryDate: "",
    hasInfluenzaVaccine: false,
    influenzaVaccineDate: "",
    hasReadLCHSFactSheet: false,
    certificateOfCurrency: null,
    certificateOfCurrencyStartDate: "",
    certificateOfCurrencyFinishDate: "",
  });

  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [viewingDoc, setViewingDoc] = useState(null); // 'police' | 'currency' | null

  // Certificate of Currency file upload state
  const [selectedCurrencyFile, setSelectedCurrencyFile] = useState(null);
  const [currencyFileToDelete, setCurrencyFileToDelete] = useState(null);
  const [showDeleteCurrencyModal, setShowDeleteCurrencyModal] = useState(false);

  // Track initial state for change detection using useRef for better performance
  const initialDataRef = React.useRef({
    policeCertificate: null,
    policeCertificateExpiryDate: "",
    hasInfluenzaVaccine: false,
    influenzaVaccineDate: "",
    hasReadLCHSFactSheet: false,
    certificateOfCurrency: null,
    certificateOfCurrencyStartDate: "",
    certificateOfCurrencyFinishDate: "",
  });

  // Check if there are unsaved changes
  const hasUnsavedChanges = React.useMemo(() => {
    return (
      selectedFile !== null ||
      fileToDelete !== null ||
      selectedCurrencyFile !== null ||
      currencyFileToDelete !== null ||
      complianceData.hasInfluenzaVaccine !== initialDataRef.current.hasInfluenzaVaccine ||
      complianceData.influenzaVaccineDate !== initialDataRef.current.influenzaVaccineDate ||
      complianceData.hasReadLCHSFactSheet !== initialDataRef.current.hasReadLCHSFactSheet ||
      complianceData.policeCertificateExpiryDate !== initialDataRef.current.policeCertificateExpiryDate ||
      complianceData.certificateOfCurrency !== initialDataRef.current.certificateOfCurrency ||
      complianceData.certificateOfCurrencyStartDate !== initialDataRef.current.certificateOfCurrencyStartDate ||
      complianceData.certificateOfCurrencyFinishDate !== initialDataRef.current.certificateOfCurrencyFinishDate
    );
  }, [complianceData, selectedFile, fileToDelete, selectedCurrencyFile, currencyFileToDelete]);


  // Load compliance data on mount
  useEffect(() => {
    // If userInfo is not yet loaded, stay in loading state and wait
    if (userInfo === null || userInfo === undefined) {
      setLoading(true);
      return;
    }
    loadComplianceData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const loadComplianceData = async () => {
    if (!userInfo?.fseId) {
      setError("Franchisee ID not found. Please contact support.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getComplianceData(userInfo.fseId);

      const loadedData = {
        policeCertificate: data.policeCertificate || null,
        policeCertificateExpiryDate: data.policeCertificateExpiryDate || "",
        hasInfluenzaVaccine: data.hasInfluenzaVaccine || false,
        influenzaVaccineDate: data.influenzaVaccineDate || "",
        hasReadLCHSFactSheet: data.hasReadLCHSFactSheet || false,
        certificateOfCurrency: data.certificateOfCurrency || null,
        certificateOfCurrencyStartDate: data.certificateOfCurrencyStartDate || "",
        certificateOfCurrencyFinishDate: data.certificateOfCurrencyFinishDate || "",
      };

      setComplianceData(loadedData);
      initialDataRef.current = loadedData;
    } catch (err) {
      console.error("Error loading compliance data:", err);
      setError("Failed to load compliance data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      event.target.value = "";
      return;
    }

    setError(null);
    setSelectedFile(file);
  };

  const handleRemoveSelectedFile = () => {
    setSelectedFile(null);
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = "";
  };

  const handleMarkForDeletion = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    setFileToDelete(complianceData.policeCertificate);
    setComplianceData((prev) => ({
      ...prev,
      policeCertificate: null,
      policeCertificateExpiryDate: "",
    }));
    setShowDeleteModal(false);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Certificate of Currency handlers
  const handleCurrencyFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      event.target.value = "";
      return;
    }

    setError(null);
    setSelectedCurrencyFile(file);
  };

  const handleRemoveSelectedCurrencyFile = () => {
    setSelectedCurrencyFile(null);
    // Reset the file input
    const fileInput = document.querySelector('input[name="currency-certificate-file"]');
    if (fileInput) fileInput.value = "";
  };

  const handleMarkCurrencyForDeletion = () => {
    setShowDeleteCurrencyModal(true);
  };

  const confirmCurrencyDelete = () => {
    setCurrencyFileToDelete(complianceData.certificateOfCurrency);
    setComplianceData((prev) => ({
      ...prev,
      certificateOfCurrency: null,
      certificateOfCurrencyStartDate: "",
      certificateOfCurrencyFinishDate: "",
    }));
    setShowDeleteCurrencyModal(false);
  };

  const cancelCurrencyDelete = () => {
    setShowDeleteCurrencyModal(false);
  };

  const handleViewDocument = async (fileUrl, docType) => {
    try {
      setViewingDoc(docType);
      const signedUrl = await getDocumentSignedUrl(fileUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error viewing document:', err);
      setError('Failed to open document: ' + err.message);
    } finally {
      setViewingDoc(null);
    }
  };

  const handleSave = async () => {
    if (!userInfo?.fseId) {
      setError("Franchisee ID not found. Please contact support.");
      return;
    }

    // Validate required fields
    if (
      (complianceData.policeCertificate || selectedFile) &&
      (!complianceData.policeCertificateExpiryDate ||
        complianceData.policeCertificateExpiryDate === "")
    ) {
      setError("Please provide an expiry date for the police certificate");
      return;
    }

    if (selectedFile && !complianceData.policeCertificateExpiryDate) {
      setError(
        "Please provide an expiry date for the uploaded police certificate"
      );
      return;
    }

    // Validate Certificate of Currency dates
    if (
      (complianceData.certificateOfCurrency || selectedCurrencyFile) &&
      (!complianceData.certificateOfCurrencyStartDate || !complianceData.certificateOfCurrencyFinishDate)
    ) {
      setError("Please provide both start and finish dates for the certificate of currency");
      return;
    }

    // Validate that start date is before finish date
    if (complianceData.certificateOfCurrencyStartDate && complianceData.certificateOfCurrencyFinishDate) {
      const startDate = new Date(complianceData.certificateOfCurrencyStartDate);
      const finishDate = new Date(complianceData.certificateOfCurrencyFinishDate);
      if (startDate >= finishDate) {
        setError("Start date must be before finish date for certificate of currency");
        return;
      }
    }

    if (
      complianceData.hasInfluenzaVaccine &&
      !complianceData.influenzaVaccineDate
    ) {
      setError("Please provide the date for the influenza vaccine");
      return;
    }

    if (
      !complianceData.hasReadLCHSFactSheet
    ) {
      setError("Please read the  Aged Care Provider Training Fact Sheet  ");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let certificateUrl = complianceData.policeCertificate;
      let certificateOfCurrencyUrl = complianceData.certificateOfCurrency;

      // Delete old file if marked for deletion
      if (fileToDelete) {
        await deletePoliceCertificate(fileToDelete);
        setFileToDelete(null);
      }

      // Upload new file if selected
      if (selectedFile) {
        certificateUrl = await uploadPoliceCertificate(selectedFile);
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = "";
      }

      // Delete old currency file if marked for deletion
      if (currencyFileToDelete) {
        await deleteCertificateOfCurrency(currencyFileToDelete);
        setCurrencyFileToDelete(null);
      }

      // Upload new currency file if selected
      if (selectedCurrencyFile) {
        certificateOfCurrencyUrl = await uploadCertificateOfCurrency(selectedCurrencyFile);
        setSelectedCurrencyFile(null);
        // Reset file input
        const currencyFileInput = document.querySelector('input[name="currency-certificate-file"]');
        if (currencyFileInput) currencyFileInput.value = "";
      }

      const dataToSave = {
        policeCertificate: certificateUrl,
        policeCertificateExpiryDate:
          complianceData.policeCertificateExpiryDate || null,
        hasInfluenzaVaccine: complianceData.hasInfluenzaVaccine,
        influenzaVaccineDate: complianceData.hasInfluenzaVaccine
          ? complianceData.influenzaVaccineDate
          : null,
        hasReadLCHSFactSheet: complianceData.hasReadLCHSFactSheet,
        lchsFactSheetDate: complianceData.hasReadLCHSFactSheet
          ? complianceData.lchsFactSheetDate
          : null,
        certificateOfCurrency: certificateOfCurrencyUrl,
        certificateOfCurrencyStartDate: complianceData.certificateOfCurrencyStartDate || null,
        certificateOfCurrencyFinishDate: complianceData.certificateOfCurrencyFinishDate || null,
      };

      await updateComplianceData(userInfo.fseId, dataToSave);

      // Update local state with new certificate URL and saved data
      const savedData = {
        ...complianceData,
        policeCertificate: certificateUrl,
        certificateOfCurrency: certificateOfCurrencyUrl,
      };

      setComplianceData(savedData);
      initialDataRef.current = savedData;

      setSuccessMessage("Compliance information saved successfully");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error saving compliance data:", err);
      setError("Failed to save compliance data: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Loading compliance information...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
          <FaExclamationTriangle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start">
          <FaCheckCircle className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
          <span className="text-sm text-green-700">{successMessage}</span>
        </div>
      )}

      {/* All Compliance Items in One Card */}
      <div className="bg-white rounded-2xl ring-1 ring-gray-900/5 shadow-sm p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Compliance Requirements
          </h3>
        </div>

        <div className="space-y-4">
          {/* Police Certificate Section */}
          <div className="pb-4 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              Police Check Certificate
              {complianceData.policeCertificate && (
                <FaCheckCircle className="ml-2 text-green-500 text-sm" />
              )}
            </h4>

            <div className="space-y-3">
              {/* Current Certificate Display */}
              {complianceData.policeCertificate && !selectedFile ? (
                <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FaFilePdf className="text-red-500 text-2xl" />
                      <div>
                        <button
                          onClick={() => handleViewDocument(complianceData.policeCertificate, 'police')}
                          disabled={viewingDoc === 'police'}
                          className="text-blue-600 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {viewingDoc === 'police' ? 'Opening...' : 'View Police Certificate'}
                        </button>
                        {complianceData.policeCertificateExpiryDate && (
                          <p className="text-sm text-gray-600">
                            Expires:{" "}
                            {new Date(
                              complianceData.policeCertificateExpiryDate
                            ).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleMarkForDeletion}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ) : selectedFile ? (
                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FaFileUpload className="text-green-600 text-2xl" />
                      <div>
                        <p className="text-gray-700 font-medium">
                          {selectedFile.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {(selectedFile.size / 1024).toFixed(2)} KB - Will be
                          uploaded on save
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveSelectedFile}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FaFileUpload className="mx-auto text-gray-400 text-4xl mb-3" />
                  <label className="cursor-pointer">
                    <span className="text-green-600 hover:text-green-500 font-medium">
                      Click to select police certificate
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Accepted formats: PDF, JPG, PNG (Max 10MB)
                  </p>
                </div>
              )}

              {/* Expiry Date Input */}
              {(complianceData.policeCertificate || selectedFile) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date *
                  </label>
                  <input
                    type="date"
                    value={complianceData.policeCertificateExpiryDate}
                    onChange={(e) =>
                      setComplianceData((prev) => ({
                        ...prev,
                        policeCertificateExpiryDate: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              )}
            </div>
          </div>

          {/* Certificate of Currency Section */}
          <div className="pb-4 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              Certificate of Currency
              {complianceData.certificateOfCurrency && (
                <FaCheckCircle className="ml-2 text-green-500 text-sm" />
              )}
            </h4>

            <div className="space-y-3">
              {/* Current Certificate Display */}
              {complianceData.certificateOfCurrency && !selectedCurrencyFile ? (
                <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FaFilePdf className="text-red-500 text-2xl" />
                      <div>
                        <button
                          onClick={() => handleViewDocument(complianceData.certificateOfCurrency, 'currency')}
                          disabled={viewingDoc === 'currency'}
                          className="text-blue-600 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {viewingDoc === 'currency' ? 'Opening...' : 'View Certificate of Currency'}
                        </button>
                        {complianceData.certificateOfCurrencyStartDate && complianceData.certificateOfCurrencyFinishDate && (
                          <p className="text-sm text-gray-600">
                            Period: {new Date(complianceData.certificateOfCurrencyStartDate).toLocaleDateString()} - {new Date(complianceData.certificateOfCurrencyFinishDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleMarkCurrencyForDeletion}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ) : selectedCurrencyFile ? (
                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FaFileUpload className="text-green-600 text-2xl" />
                      <div>
                        <p className="text-gray-700 font-medium">
                          {selectedCurrencyFile.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {(selectedCurrencyFile.size / 1024).toFixed(2)} KB - Will be uploaded on save
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveSelectedCurrencyFile}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FaFileUpload className="mx-auto text-gray-400 text-4xl mb-3" />
                  <label className="cursor-pointer">
                    <span className="text-green-600 hover:text-green-500 font-medium">
                      Click to select certificate of currency
                    </span>
                    <input
                      type="file"
                      name="currency-certificate-file"
                      className="hidden"
                      onChange={handleCurrencyFileSelect}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Accepted formats: PDF, JPG, PNG (Max 10MB)
                  </p>
                </div>
              )}

              {/* Date Inputs */}
              {(complianceData.certificateOfCurrency || selectedCurrencyFile) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={complianceData.certificateOfCurrencyStartDate}
                      onChange={(e) =>
                        setComplianceData((prev) => ({
                          ...prev,
                          certificateOfCurrencyStartDate: e.target.value,
                        }))
                      }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Finish Date *
                    </label>
                    <input
                      type="date"
                      value={complianceData.certificateOfCurrencyFinishDate}
                      onChange={(e) =>
                        setComplianceData((prev) => ({
                          ...prev,
                          certificateOfCurrencyFinishDate: e.target.value,
                        }))
                      }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Influenza Vaccine Declaration */}
          <div className="pb-4 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              Influenza Vaccine Declaration
              {complianceData.hasInfluenzaVaccine && (
                <FaCheckCircle className="ml-2 text-green-500 text-sm" />
              )}
            </h4>

            <div className="space-y-4">
              <div className="flex items-start">
                <input
                  type="radio"
                  id="influenza-vaccine-no"
                  name="influenzaVaccine"
                  checked={!complianceData.hasInfluenzaVaccine}
                  onChange={() => {
                    setComplianceData((prev) => ({
                      ...prev,
                      hasInfluenzaVaccine: false,
                      influenzaVaccineDate: "",
                    }));
                  }}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label
                  htmlFor="influenza-vaccine-no"
                  className="ml-3 text-gray-700"
                >
                  No, I have not had the flu vaccine
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="radio"
                  id="influenza-vaccine-yes"
                  name="influenzaVaccine"
                  checked={complianceData.hasInfluenzaVaccine}
                  onChange={() => {
                    setComplianceData((prev) => ({
                      ...prev,
                      hasInfluenzaVaccine: true,
                    }));
                  }}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label
                  htmlFor="influenza-vaccine-yes"
                  className="ml-3 text-gray-700"
                >
                  Yes, I have had the flu vaccine
                </label>
              </div>

              {complianceData.hasInfluenzaVaccine && (
                <div className="ml-7">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vaccination Date *
                  </label>
                  <input
                    type="date"
                    value={complianceData.influenzaVaccineDate}
                    onChange={(e) =>
                      setComplianceData((prev) => ({
                        ...prev,
                        influenzaVaccineDate: e.target.value,
                      }))
                    }
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              )}
            </div>
          </div>

          {/* LCHS Fact Sheet Declaration */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              Aged Care Provider Training
              {complianceData.hasReadLCHSFactSheet && (
                <FaCheckCircle className="ml-2 text-green-500 text-sm" />
              )}
            </h4>

            <div className="space-y-4">
              {/* Fact Sheet Content */}
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <button
                  onClick={() => setShowFactSheet(!showFactSheet)}
                  className="text-green-600 hover:text-green-500 font-medium mb-2"
                >
                  {showFactSheet ? "Hide" : "Show"} Fact Sheet Content
                </button>

                {showFactSheet && (
                  <div className="mt-3 text-sm text-gray-700 whitespace-pre-line border-t border-gray-300 pt-3">
                    {LCHS_FACT_SHEET_TEXT}
                  </div>
                )}
              </div>

              {/* Declaration Checkbox */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="lchs-fact-sheet"
                  checked={complianceData.hasReadLCHSFactSheet}
                  onChange={(e) => {
                    setComplianceData((prev) => ({
                      ...prev,
                      hasReadLCHSFactSheet: e.target.checked,
                    }));
                  }}
                  className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="lchs-fact-sheet" className="ml-3 text-gray-700">
                  I have read and understood the attached Aged Care
                  Provider Training Fact Sheet
                </label>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="text-xs text-gray-500">
              {hasUnsavedChanges ? (
                <span className="flex items-center text-orange-600">
                  <FaExclamationTriangle className="mr-1" />
                  You have unsaved changes
                </span>
              ) : (
                <span className="flex items-center text-green-600">
                  <FaCheckCircle className="mr-1" />
                  All changes saved
                </span>
              )}
            </div>
            <button
              onClick={async () => await handleSave()}
              disabled={saving || !hasUnsavedChanges}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-full hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Police Certificate
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this police certificate? This action will be completed when you save your changes.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal for Certificate of Currency */}
      {showDeleteCurrencyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Certificate of Currency
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this certificate of currency? This action will be completed when you save your changes.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelCurrencyDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmCurrencyDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceTab;
